// Contexto de Notificaciones
import React, { createContext, useContext, ReactNode } from 'react';
import { SnackbarProvider, useSnackbar, VariantType } from 'notistack';

// ── Sonidos de notificación via Web Audio API ─────────────────────────────
function playNotificationSound(variant: VariantType) {
  try {
    const prefs = JSON.parse(localStorage.getItem('cn_user_preferences') || '{}');
    if (prefs.soundEffects === false) return;

    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    // Frecuencia y duración según tipo
    const config: Record<string, { freq: number; dur: number; type: OscillatorType }> = {
      success: { freq: 880,  dur: 0.15, type: 'sine' },
      error:   { freq: 280,  dur: 0.25, type: 'sawtooth' },
      warning: { freq: 520,  dur: 0.20, type: 'sine' },
      info:    { freq: 660,  dur: 0.15, type: 'sine' },
      default: { freq: 440,  dur: 0.15, type: 'sine' },
    };
    const { freq, dur, type } = config[variant as string] ?? config.default;
    osc.frequency.value = freq;
    osc.type = type;
    gain.gain.setValueAtTime(0.07, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    osc.start();
    osc.stop(ctx.currentTime + dur);
  } catch {
    // Web Audio no disponible: silencioso
  }
}

interface NotificationContextType {
  showNotification: (message: string, variant?: VariantType) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

const NotificationProviderInner: React.FC<NotificationProviderProps> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  const showNotification = (message: string, variant: VariantType = 'default') => {
    playNotificationSound(variant);
    enqueueSnackbar(message, { variant });
  };

  const showSuccess = (message: string) => {
    playNotificationSound('success');
    enqueueSnackbar(message, { variant: 'success' });
  };

  const showError = (message: string) => {
    playNotificationSound('error');
    enqueueSnackbar(message, { variant: 'error' });
  };

  const showWarning = (message: string) => {
    playNotificationSound('warning');
    enqueueSnackbar(message, { variant: 'warning' });
  };

  const showInfo = (message: string) => {
    playNotificationSound('info');
    enqueueSnackbar(message, { variant: 'info' });
  };

  const value: NotificationContextType = {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      autoHideDuration={4000}
    >
      <NotificationProviderInner>{children}</NotificationProviderInner>
    </SnackbarProvider>
  );
};

/**
 * Hook para usar el contexto de notificaciones
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification debe ser usado dentro de un NotificationProvider');
  }
  return context;
};

export default NotificationContext;
